/* =================================================================================================
 * Page « Nouvelle collection » — bande de polaroïds.
 *
 * Défilement automatique ET manipulable à la main, en boucle sans fin dans les
 * deux sens.
 *
 * Trois principes, chacun tiré d'un échec précédent :
 *
 * 1. Un seul mécanisme. Le défilement automatique avance `scrollLeft`, celui
 *    du visiteur aussi. Une animation CSS en parallèle fige la position et
 *    empêche tout défilement natif : les deux ne peuvent pas coexister.
 *
 * 2. Trois copies de la série. Le navigateur bloque `scrollLeft` à 0 : avec
 *    deux copies on ne peut pas tirer vers l'arrière depuis le début. En
 *    maintenant la position dans la copie du milieu, il reste toujours un
 *    cycle entier de marge de chaque côté — aucun bord n'est jamais atteint.
 *
 * 3. Ne jamais suspendre sur l'événement `scroll`. Le défilement automatique
 *    le déclenche lui-même à chaque image : la bande se mettrait en pause en
 *    boucle et avancerait par à-coups. On ne réagit qu'aux gestes réels.
 *
 * ⚠️ La balise `campus-polaroids` doit porter `display: block` : une balise sur
 * mesure est « en ligne » par défaut, ce qui fait disparaître le fond vert.
 * ===============================================================================================*/

(function () {
  'use strict';

  if (customElements.get('campus-polaroids')) return;

  var REPRISE_PAR_DEFAUT = 400; // ms avant que le défilement reparte

  class CampusPolaroids extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.campus-polaroids__viewport');
      this.track = this.querySelector('.campus-polaroids__track');
      if (!this.viewport || !this.track) return;

      this.duree = parseFloat(this.dataset.duration) || 90;
      this.delaiReprise = parseFloat(this.dataset.resume);
      if (isNaN(this.delaiReprise)) this.delaiReprise = REPRISE_PAR_DEFAUT;
      this.suspendu = false;
      this.visible = true;
      this.glisseEnCours = false;
      this.reprise = null;
      this.dernierTemps = 0;

      this.viewport.addEventListener('pointerdown', this.debutGlisse.bind(this));
      this.viewport.addEventListener('pointermove', this.glisse.bind(this));
      this.viewport.addEventListener('pointerup', this.finGlisse.bind(this));
      this.viewport.addEventListener('pointercancel', this.finGlisse.bind(this));

      // Gestes réels uniquement — surtout pas `scroll` (voir l'en-tête).
      this.viewport.addEventListener('touchstart', this.suspendre.bind(this), { passive: true });
      this.viewport.addEventListener('wheel', this.suspendre.bind(this), { passive: true });

      if (window.IntersectionObserver) {
        this.observer = new IntersectionObserver((e) => { this.visible = e[0].isIntersecting; });
        this.observer.observe(this);
      }

      this.mouvementReduit = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Position de départ : début de la copie du milieu. Les photos étant
      // chargées en différé, la largeur de la piste n'est pas connue tout de
      // suite : on replace donc à chaque image reçue, sans quoi le cycle serait
      // calculé sur une piste incomplète.
      this.placerAuMilieu = this.placerAuMilieu.bind(this);
      this.placerAuMilieu();
      this.track.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', this.placerAuMilieu, { once: true });
        img.addEventListener('error', this.placerAuMilieu, { once: true });
      });
      window.addEventListener('resize', this.placerAuMilieu);

      this.boucle = this.avancer.bind(this);
      this.frame = window.requestAnimationFrame(this.boucle);
    }

    disconnectedCallback() {
      if (this.frame) window.cancelAnimationFrame(this.frame);
      if (this.observer) this.observer.disconnect();
      window.clearTimeout(this.reprise);
    }

    /**
     * Longueur d'un cycle : la piste compte 3N cartes et 3N−1 écarts, un cycle
     * en vaut N et N — soit (largeur totale + un écart) / 3.
     * @returns {number}
     */
    longueurCycle() {
      var ecart = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
      var total = this.track.scrollWidth;
      return total ? (total + ecart) / 3 : 0;
    }

    placerAuMilieu() {
      var cycle = this.longueurCycle();
      if (cycle) this.viewport.scrollLeft = cycle;
    }

    /**
     * Ramène la position dans la copie du milieu. Les trois copies étant
     * identiques, le report d'un cycle est invisible — c'est ce qui donne la
     * boucle sans fin, dans les deux sens.
     */
    normaliser() {
      var cycle = this.longueurCycle();
      // Tant que la piste n'excède pas la fenêtre, il n'y a rien à reporter —
      // et forcer une position que le navigateur ramènerait à zéro ferait
      // osciller la bande.
      if (!cycle || this.track.scrollWidth <= this.viewport.clientWidth + 4) return;
      var x = this.viewport.scrollLeft;
      if (x >= 2 * cycle) this.viewport.scrollLeft = x - cycle;
      else if (x < cycle) this.viewport.scrollLeft = x + cycle;
    }

    avancer(temps) {
      var delta = this.dernierTemps ? temps - this.dernierTemps : 0;
      this.dernierTemps = temps;

      var enMarche = !this.suspendu && !this.glisseEnCours
        && this.visible && !this.mouvementReduit;

      if (enMarche) {
        var cycle = this.longueurCycle();
        // `delta` borné : après un onglet en arrière-plan il peut valoir
        // plusieurs secondes, la bande ferait un bond.
        if (cycle) this.viewport.scrollLeft += (cycle / (this.duree * 1000)) * Math.min(delta, 64);
      }

      this.normaliser();
      this.frame = window.requestAnimationFrame(this.boucle);
    }

    /**
     * Met le défilement en pause, puis le relance de lui-même. Un court délai
     * plutôt qu'une reprise immédiate : au doigt, le défilement natif garde son
     * inertie après le lâcher, et repartir aussitôt la contrarierait.
     */
    suspendre() {
      this.suspendu = true;
      window.clearTimeout(this.reprise);
      this.reprise = window.setTimeout(() => { this.suspendu = false; }, this.delaiReprise);
    }

    /* ------------------------------------------------- glisser à la souris */

    debutGlisse(evt) {
      // Le tactile a son propre défilement, avec inertie : on le laisse faire.
      if (evt.pointerType === 'touch') return;
      evt.preventDefault(); // pas de sélection de texte pendant le geste
      this.glisseEnCours = true;
      this.dernierX = evt.clientX;
      this.viewport.setPointerCapture(evt.pointerId);
      this.classList.add('is-grabbing');
    }

    glisse(evt) {
      if (!this.glisseEnCours) return;
      evt.preventDefault();
      // Déplacement relatif au dernier point, et non à un point de départ fixe :
      // le report d'un cycle ne décale donc pas le geste en cours.
      this.viewport.scrollLeft -= evt.clientX - this.dernierX;
      this.dernierX = evt.clientX;
    }

    finGlisse(evt) {
      if (!this.glisseEnCours) return;
      this.glisseEnCours = false;
      this.classList.remove('is-grabbing');
      if (this.viewport.hasPointerCapture(evt.pointerId)) {
        this.viewport.releasePointerCapture(evt.pointerId);
      }
      this.suspendre();
    }
  }

  customElements.define('campus-polaroids', CampusPolaroids);
})();
